import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useProgramList, useDeleteProgram, useToggleProgramStatus } from '@/hooks/useProgram';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { BulkSelectionToolbar } from '@/components/list/BulkSelectionToolbar';
import { useBulkSelection } from '@/hooks/useBulkSelection';
import {
  Search,
  Plus,
  MoreHorizontal,
  Trash2,
  Power,
  PowerOff,
  Target,
  Calendar,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';

const getStatusBadgeColor = (status: string) => {
  const colors: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
    active: 'default',
    inactive: 'secondary',
    completed: 'outline',
    cancelled: 'destructive',
  };
  return colors[status] || 'outline';
};

const getStatusLabel = (status: string) => {
  const labels: Record<string, string> = {
    active: 'Aktif',
    inactive: 'Tidak Aktif',
    completed: 'Selesai',
    cancelled: 'Dibatalkan',
  };
  return labels[status] || status;
};

export function ProgramList() {
  const [activeTab, setActiveTab] = useState<'utama' | 'khusus'>('utama');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [pendingDeleteIds, setPendingDeleteIds] = useState<number[]>([]);
  const navigate = useNavigate();

  const { data, isLoading } = useProgramList({ page, limit: 10, search, status: statusFilter, jenisProgram: activeTab });
  const deleteProgram = useDeleteProgram();
  const toggleStatus = useToggleProgramStatus();
  const selection = useBulkSelection((data?.data || []).map((item: any) => item.id));

  const handleHapus = async () => {
    if (pendingDeleteIds.length) {
      for (const id of pendingDeleteIds) {
        await deleteProgram.mutateAsync(id);
      }
      selection.clearSelection();
      setPendingDeleteIds([]);
    }
  };

  const handleToggleStatus = async (id: number) => {
    await toggleStatus.mutateAsync(id);
  };

  const createProgramLabel = activeTab === 'utama' ? 'Tambah Program Utama' : 'Tambah Program Khusus';
  const activeTabTitle = activeTab === 'utama' ? 'Program Utama' : 'Program Khusus';

  const activePrograms = data?.data.filter((p: any) => p.status === 'active').length || 0;
  const totalTarget = data?.data.reduce((sum: number, p: any) => sum + (Number(p.targetDonasi) || 0), 0) || 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Manajemen Program</h2>
          <p className="text-muted-foreground">Kelola program dan kampanye donasi</p>
        </div>
        <Link to={`/program/create?jenis=${activeTab}`}>
          <Button>
            <Plus className="mr-2 h-4 w-4" />
            {createProgramLabel}
          </Button>
        </Link>
      </div>

      <Tabs value={activeTab} onValueChange={(value) => { setActiveTab(value as 'utama' | 'khusus'); setPage(1); selection.clearSelection(); }}>
        <TabsList>
          <TabsTrigger value="utama">Program Utama</TabsTrigger>
          <TabsTrigger value="khusus">Program Khusus</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Program</CardTitle>
            <Target className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{data?.total || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Program Aktif</CardTitle>
            <Power className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{activePrograms}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Target</CardTitle>
            <Target className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">Rp {totalTarget.toLocaleString('id-ID')}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Bulan Ini</CardTitle>
            <Calendar className="h-4 w-4 text-purple-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-600">
              {data?.data.filter((p: any) => {
                const date = p.createdAt ? new Date(p.createdAt) : null;
                const now = new Date();
                return date && date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
              }).length || 0}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filter {activeTabTitle}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Cari program..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8"
              />
            </div>
            <Select value={statusFilter} onValueChange={(value) => setStatusFilter(value === '__all__' ? '' : value)}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filter berdasarkan status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Semua Status</SelectItem>
                <SelectItem value="active">Aktif</SelectItem>
                <SelectItem value="inactive">Tidak Aktif</SelectItem>
                <SelectItem value="completed">Selesai</SelectItem>
                <SelectItem value="cancelled">Dibatalkan</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <BulkSelectionToolbar
            className="m-4 mb-0"
            selectedCount={selection.selectedCount}
            itemLabel="program"
            onClear={selection.clearSelection}
            onDelete={() => setPendingDeleteIds(selection.selectedIds)}
            deleteDisabled={deleteProgram.isPending}
          />
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">
                  <Checkbox checked={selection.allSelected} onCheckedChange={(checked) => selection.toggleAll(Boolean(checked))} />
                </TableHead>
                <TableHead>Kode</TableHead>
                <TableHead>Nama Program</TableHead>
                <TableHead>Jenis</TableHead>
                <TableHead>Kategori</TableHead>
                <TableHead>Gambar</TableHead>
                <TableHead>Periode</TableHead>
                <TableHead>Target</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={10} className="py-8 text-center">Memuat...</TableCell>
                </TableRow>
              ) : data?.data.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} className="py-8 text-center">Belum ada program.</TableCell>
                </TableRow>
              ) : (
                data?.data.map((program: any) => (
                  <TableRow key={program.id} className={selection.isSelected(program.id) ? 'bg-muted/30' : undefined}>
                    <TableCell>
                      <Checkbox checked={selection.isSelected(program.id)} onCheckedChange={(checked) => selection.toggleOne(program.id, Boolean(checked))} />
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{program.kode}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{program.nama}</div>
                      {program.deskripsi && (
                        <div className="max-w-xs truncate text-sm text-muted-foreground">{program.deskripsi}</div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={program.jenisProgram === 'utama' ? 'default' : 'secondary'}>
                        {program.jenisProgram === 'utama' ? 'Utama' : 'Khusus'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {program.kategoriProgram ? (
                        <Badge variant="outline" className="capitalize">
                          {program.kategoriProgram}
                        </Badge>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>
                      {program.cover ? (
                        <img src={program.cover} alt={program.nama} className="h-12 w-20 rounded object-cover" />
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1 text-sm">
                        <div>
                          Mulai: {program.tanggalMulai ? new Date(program.tanggalMulai).toLocaleDateString('id-ID') : '-'}
                        </div>
                        {program.jenisProgram === 'khusus' ? (
                          <div>
                            Selesai: {program.tanggalSelesai ? new Date(program.tanggalSelesai).toLocaleDateString('id-ID') : '-'}
                          </div>
                        ) : null}
                      </div>
                    </TableCell>
                    <TableCell>
                      {program.targetDonasi ? (
                        <span className="font-medium">Rp {Number(program.targetDonasi).toLocaleString('id-ID')}</span>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={getStatusBadgeColor(program.status)}>{getStatusLabel(program.status)}</Badge>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/program/${program.id}/edit`)}>Edit</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleToggleStatus(program.id)}>
                            {program.status === 'active' ? (
                              <>
                                <PowerOff className="mr-2 h-4 w-4" />
                                Nonaktifkan
                              </>
                            ) : (
                              <>
                                <Power className="mr-2 h-4 w-4" />
                                Aktifkan
                              </>
                            )}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setPendingDeleteIds([program.id])} className="text-red-600">
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <div className="flex items-center justify-end gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={page === 1}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="text-sm text-muted-foreground">Halaman {page} dari {Math.max(1, Math.ceil((data?.total || 0) / 10))}</span>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setPage((p) => p + 1)}
          disabled={page >= Math.max(1, Math.ceil((data?.total || 0) / 10))}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      <Dialog open={pendingDeleteIds.length > 0} onOpenChange={() => setPendingDeleteIds([])}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Yakin ingin melanjutkan?</DialogTitle>
            <DialogDescription>Tindakan ini tidak dapat dibatalkan. Data program akan dihapus permanen.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingDeleteIds([])}>Batal</Button>
            <Button variant="destructive" onClick={handleHapus}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
